import { LitElement, html } from '@hh/global.js'

class CollectionFilterDrawer extends LitElement {
  static properties = {
    open: {
      type: Boolean,
    },
  }

  constructor() {
    super()
  }

  connectedCallback() {
    super.connectedCallback()
    this.handleCloseTrigger()
    this.handleShowMoreButtons()
    this.handleTooltips()
    this.handleEscKey()

    document.addEventListener('plp:filter-update', () => {
      this.handleCloseTrigger()
      this.handleShowMoreButtons()
      this.handleTooltips()
    })
  }

  disconnectedCallback() {
    super.disconnectedCallback()
  }

  isMobile() {
    return !window.matchMedia('(min-width: 768px)').matches
  }

  updated() {
    if (this.open && this.isMobile()) {
      document.body.classList.add('overflow-hidden')
    } else if (!this.open && this.isMobile()) {
      document.body.classList.remove('overflow-hidden')
    }
  }

  hideAllTooltips() {
    // find all tooltips in document.body (where they may have been moved)
    const tooltipsInBody = Array.from(
      document.querySelectorAll(
        '[data-filter-option-tooltip-content-mobile], [data-filter-option-tooltip-content-desktop]'
      )
    ).filter((tooltip) => tooltip.parentNode === document.body)

    // restore tooltips to their original parents and hide them
    tooltipsInBody.forEach((tooltip) => {
      if (tooltip._originalParent) {
        tooltip._originalParent.appendChild(tooltip)
        tooltip._originalParent = null
      }
      tooltip.style.display = 'none'
    })

    // also hide tooltips that are still in the component
    const tooltipsInComponent = this.querySelectorAll(
      '[data-filter-option-tooltip-content-mobile], [data-filter-option-tooltip-content-desktop]'
    )
    tooltipsInComponent.forEach((tooltip) => {
      tooltip.style.display = 'none'
    })
  }

  closeDrawer() {
    const filterButtonsEl = document.querySelectorAll(
      '.js-toggle-filter-button'
    )
    const filterDrawerEl = document.querySelector('collection-filter-drawer')
    const productGridContainer = document.querySelector(
      '.js-collection-product-grid-container'
    )
    filterDrawerEl.removeAttribute('open')
    productGridContainer.classList.remove('collection-product-grid__narrow')

    filterButtonsEl?.forEach((el) => {
      el.classList.remove('is-show')
    })

    this.hideAllTooltips()
  }

  handleCloseTrigger() {
    const filterDrawerClose = this.querySelectorAll(
      '.js-collection-filter-drawer-close'
    )

    filterDrawerClose?.forEach((el) => {
      el.addEventListener('click', () => {
        this.closeDrawer()
      })
    })
  }

  handleShowMoreButtons() {
    const showMoreButtons = this.querySelectorAll(
      '[data-show-more-options-button]'
    )

    showMoreButtons.forEach((button) => {
      const newButton = button.cloneNode(true)
      button.parentNode.replaceChild(newButton, button)

      newButton.addEventListener('click', (e) => {
        e.preventDefault()
        const filterId = newButton.dataset.filterId
        const filterDetail = this.querySelector(
          `.js-filter[data-filter-id="${filterId}"]`
        )

        if (!filterDetail) return

        const hiddenItems = filterDetail.querySelectorAll(
          '[data-initally-hidden-filter-item]'
        )

        hiddenItems.forEach((item) => {
          item.classList.remove('collection-filter-drawer__filter-item--hidden')
        })

        newButton.style.display = 'none'
      })
    })
  }

  showTooltip({ icon, tooltipContent, device, filterItem, event }) {
    tooltipContent._originalParent = filterItem
    document.body.appendChild(tooltipContent)
    tooltipContent.style.display = 'block'

    const isToplevelTooltip = icon.hasAttribute(
      'data-filter-option-toplevel-tooltip'
    )

    if (isToplevelTooltip && device === 'mobile') {
      event.preventDefault()
    }

    if (device === 'desktop') {
      if (isToplevelTooltip) {
        const iconRect = icon.getBoundingClientRect()
        tooltipContent.style.left = `${iconRect.right}px`
        tooltipContent.style.top = `${
          iconRect.top + iconRect.height / 2 - tooltipContent.offsetHeight / 2
        }px`
      } else {
        const rect = filterItem.getBoundingClientRect()
        tooltipContent.style.left = `${rect.right}px`
        tooltipContent.style.top = `${
          rect.top + rect.height / 2 - tooltipContent.offsetHeight / 2
        }px`
      }
    }
  }

  handleTooltips() {
    const tooltipIcons = this.querySelectorAll(
      '[data-filter-option-tooltip-icon-desktop]'
    )

    const mobileTooltipIcons = this.querySelectorAll(
      '[data-filter-option-tooltip-icon-mobile]'
    )

    mobileTooltipIcons.forEach((icon) => {
      const filterItem = icon.closest('[data-tooltip-parent]')

      const tooltipContent = filterItem?.querySelector(
        '[data-filter-option-tooltip-content-mobile]'
      )

      if (!tooltipContent) {
        return
      }

      icon.addEventListener('click', (e) =>
        this.showTooltip({
          icon,
          tooltipContent,
          device: 'mobile',
          filterItem,
          event: e,
        })
      )
    })

    tooltipIcons.forEach((icon) => {
      const filterItem = icon.closest('[data-tooltip-parent]')
      const tooltipContent = filterItem?.querySelector(
        '[data-filter-option-tooltip-content-desktop]'
      )
      if (!tooltipContent) {
        return
      }

      let hideTimeoutId = null

      const hideTooltip = () => {
        if (hideTimeoutId) {
          clearTimeout(hideTimeoutId)
          hideTimeoutId = null
        }
        if (tooltipContent.parentNode === document.body) {
          filterItem.appendChild(tooltipContent)
        }
        tooltipContent.style.display = 'none'
      }

      const scheduleHide = (event) => {
        const relatedTarget = event?.relatedTarget
        const isMovingToTooltip =
          relatedTarget && tooltipContent.contains(relatedTarget)
        const isMovingToIcon = relatedTarget && icon.contains(relatedTarget)

        if (isMovingToTooltip || isMovingToIcon) {
          return
        }

        if (hideTimeoutId) {
          clearTimeout(hideTimeoutId)
        }
        hideTimeoutId = setTimeout(hideTooltip, 100)
      }

      const cancelScheduledHide = () => {
        if (hideTimeoutId) {
          clearTimeout(hideTimeoutId)
          hideTimeoutId = null
        }
      }

      icon.addEventListener('focus', (e) =>
        this.showTooltip({
          icon,
          tooltipContent,
          device: 'desktop',
          filterItem,
          event: e,
        })
      )
      icon.addEventListener('blur', scheduleHide)
      icon.addEventListener('mouseenter', (e) => {
        cancelScheduledHide()
        this.showTooltip({
          icon,
          tooltipContent,
          device: 'desktop',
          filterItem,
          event: e,
        })
      })
      icon.addEventListener('mouseleave', scheduleHide)
      tooltipContent.addEventListener('mouseenter', cancelScheduledHide)
      tooltipContent.addEventListener('mouseleave', scheduleHide)
    })

    this.setupTooltipClickOutsideHandler()
  }

  setupTooltipClickOutsideHandler() {
    if (this._tooltipClickOutsideBound) {
      return
    }
    this._tooltipClickOutsideBound = true

    document.addEventListener(
      'click',
      (e) => {
        const target = e.target
        const isClickOnTooltipOrIcon = target.closest(
          '[data-filter-option-tooltip-content-desktop], [data-filter-option-tooltip-icon-desktop]'
        )
        const hasVisibleDesktopTooltip = document.querySelector(
          '[data-filter-option-tooltip-content-desktop][style*="display: block"]'
        )
        if (!isClickOnTooltipOrIcon && hasVisibleDesktopTooltip) {
          this.hideAllTooltips()
        }
      },
      true
    )

    window.addEventListener('blur', () => this.hideAllTooltips())
  }

  handleEscKey() {
    this.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        this.closeDrawer()
      }
    })
  }

  render() {
    return html`<hh-focus-trap active="${!!this.open}" style="height: 100%">
      <slot></slot>
    </hh-focus-trap>`
  }
}

customElements.define('collection-filter-drawer', CollectionFilterDrawer)
